import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Modal, Fade, Backdrop, CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AccessTime as ClockIcon, Error as ErrorIcon } from '@mui/icons-material';
import { apiService } from '../services/api/ApiService';
import axios from 'axios'; // Asegúrate de importar axios si lo usás

// Constants
const SESSION_TIMEOUT_SECONDS = 60;
const AUTO_LOGOUT_DELAY = 3000;
const STORAGE_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  USER_PROFILE: 'userProfile',
  REDIRECT_PATH: 'redirectPathAfterRefresh',
} as const;

const COLORS = {
  PRIMARY: '#002E74',
  PRIMARY_HOVER: '#001c46',
  TEXT_SECONDARY: '#4A4A4A',
  WHITE: 'white',
  BACKDROP: 'rgba(0, 0, 0, 0.7)',
  ERROR: '#d32f2f',
} as const;

// Utils
const sessionStorageUtil = {
  get: (key: string) => {
    const value = sessionStorage.getItem(key);
    console.log(`[SessionStorage] Get ${key}:`, value ? '*****' : 'null');
    return value;
  },
  set: (key: string, value: string) => {
    console.log(`[SessionStorage] Set ${key}:`, '*****');
    sessionStorage.setItem(key, value);
  },
  remove: (key: string) => {
    console.log(`[SessionStorage] Remove ${key}`);
    sessionStorage.removeItem(key);
  },
};

const localStorageUtil = {
  get: (key: string) => {
    const value = localStorage.getItem(key);
    console.log(`[LocalStorage] Get ${key}:`, value ? '*****' : 'null');
    return value;
  },
  set: (key: string, value: string) => {
    console.log(`[LocalStorage] Set ${key}:`, '*****');
    localStorage.setItem(key, value);
  },
  remove: (key: string) => {
    console.log(`[LocalStorage] Remove ${key}`);
    localStorage.removeItem(key);
  },
};

const clearUserSession = () => {
  console.log('[Session] Clearing user session');
  sessionStorageUtil.remove(STORAGE_KEYS.TOKEN);
  sessionStorageUtil.remove(STORAGE_KEYS.REFRESH_TOKEN);
  localStorageUtil.remove(STORAGE_KEYS.REFRESH_TOKEN);
  localStorageUtil.remove(STORAGE_KEYS.USER_PROFILE);
};

const validateRefreshToken = (): boolean => {
  let token = sessionStorageUtil.get(STORAGE_KEYS.REFRESH_TOKEN);
  if (!token) {
    console.warn('[Session] No refresh token in sessionStorage. Trying localStorage.');
    token = localStorageUtil.get(STORAGE_KEYS.REFRESH_TOKEN);
    if (token) {
      sessionStorageUtil.set(STORAGE_KEYS.REFRESH_TOKEN, token);
    }
  }
  if (!token) {
    console.error('[Session] No refresh token found');
    return false;
  }
  return true;
};

const getProgressValue = (remaining: number): number =>
  ((SESSION_TIMEOUT_SECONDS - remaining) / SESSION_TIMEOUT_SECONDS) * 100;

interface SessionClosedProps {
  open: boolean;
  onClose: () => void;
  onStayLoggedIn?: () => void;
}

const SessionClosed: React.FC<SessionClosedProps> = ({ open, onClose, onStayLoggedIn }) => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(SESSION_TIMEOUT_SECONDS);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    console.log('[Session] Modal open - starting countdown');
    setTimeLeft(SESSION_TIMEOUT_SECONDS);
    setError(null);

    if (!validateRefreshToken()) {
      setError('No hay token de refresco disponible');
      setTimeout(() => handleLogout(), AUTO_LOGOUT_DELAY);
      return;
    }

    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          handleLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  const handleLogout = useCallback(() => {
    console.log('[Session] Logout triggered');
    clearUserSession();
    onClose?.();
    navigate('/');
  }, [navigate, onClose]);

  const handleStayLoggedIn = useCallback(async () => {
    console.log('[Session] Iniciando proceso de renovación de sesión...');

    if (!validateRefreshToken()) {
      console.error('[Session] ❌ Validación fallida: No hay refresh token disponible');
      setError('Token inválido o ausente');
      return;
    }
    console.log('[Session] ✅ Refresh token validado correctamente');

    setIsRefreshing(true);
    setError(null);
    console.log('[Session] 🌀 Estado actualizado: isRefreshing=true');

    try {
      console.log('[Session] 🔄 Enviando petición para refrescar token...');
      const response = await apiService.refreshAccessToken();
      console.log('[Session] 📥 Respuesta recibida del servidor:', {
        token: response?.token ? '*****' : null,
        refreshToken: response?.refreshToken ? '*****' : null,
      });

      const token = response?.token;
      const refreshToken = response?.refreshToken;

      if (!token || !refreshToken) {
        throw new Error('Tokens inválidos del servidor');
      }
      console.log('[Session] ✅ Tokens recibidos del servidor (ocultos por seguridad)');

      // apiService.refreshAccessToken() ya guarda los tokens en sessionStorage y actualiza su instancia de axios
      // Solo necesitamos guardar el refreshToken en localStorage como respaldo
      console.log('[Session] 💾 Guardando refreshToken en localStorage como respaldo...');
      localStorageUtil.set(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      console.log('[Session] 🔐 Tokens almacenados correctamente');

      // Actualizar también la instancia global de axios para mantener consistencia
      // (aunque apiService ya actualizó su propia instancia)
      console.log('[Session] 🔄 Actualizando header de autorización de Axios global...');
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      console.log('[Session] ✅ Header de Axios actualizado');

      console.log('[Session] 🚀 Sesión renovada. Cerrando modal...');
      onStayLoggedIn?.();
      onClose?.();
      navigate('/dashboard');
      console.log('[Session] ✔️ Redireccionando a página principal');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error al renovar la sesión';
      console.error('[Session] ❌ Error durante el refresh:', {
        error: errorMsg,
        stack: err instanceof Error ? err.stack : undefined,
      });
      setError(errorMsg);
      console.log(`[Session] ⏳ Programando cierre automático en ${AUTO_LOGOUT_DELAY}ms...`);
      setTimeout(() => {
        console.log('[Session] 🔴 Ejecutando logout automático por fallo en refresh');
        handleLogout();
      }, AUTO_LOGOUT_DELAY);
    } finally {
      console.log('[Session] 🏁 Finalizando estado de carga (isRefreshing=false)');
      setIsRefreshing(false);
    }
  }, [navigate, onClose, onStayLoggedIn, handleLogout]);

  // Styles
  const styles = {
    overlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: COLORS.BACKDROP,
      zIndex: 9999,
      color: COLORS.WHITE,
      flexDirection: 'column' as const,
    },
    content: {
      bgcolor: COLORS.WHITE,
      p: 4,
      borderRadius: '8px',
      textAlign: 'center' as const,
      boxShadow: 3,
      maxWidth: '450px',
      width: '90%',
    },
    progressContainer: {
      position: 'relative' as const,
      display: 'inline-flex',
      mb: 2,
    },
    progressText: {
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      position: 'absolute' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorBox: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#ffeeee',
      borderRadius: '4px',
      p: 1.5,
      mb: 2,
    },
    errorText: {
      color: COLORS.ERROR,
      ml: 1,
    },
    buttonGroup: {
      display: 'flex',
      justifyContent: 'center',
      gap: 2,
      mt: 2,
    },
  };

  const buttonStyles = {
    outlined: {
      borderColor: COLORS.PRIMARY,
      color: COLORS.PRIMARY,
      '&:hover': {
        borderColor: COLORS.PRIMARY_HOVER,
        backgroundColor: 'rgba(0, 46, 116, 0.04)',
      },
    },
    contained: {
      backgroundColor: COLORS.PRIMARY,
      color: COLORS.WHITE,
      '&:hover': {
        backgroundColor: COLORS.PRIMARY_HOVER,
      },
    },
  };

  return (
    <Modal
      open={open}
      closeAfterTransition
      disableEscapeKeyDown
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={open}>
        <Box sx={styles.overlay}>
          <Box sx={styles.content}>
            <Typography variant="h5" sx={{ color: COLORS.PRIMARY, mb: 2 }}>
              <ClockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              ¡Alerta de sesión!
            </Typography>

            <Box sx={styles.progressContainer}>
              <CircularProgress
                variant="determinate"
                value={getProgressValue(timeLeft)}
                size={60}
                thickness={4}
                sx={{ color: COLORS.PRIMARY }}
              />
              <Box sx={styles.progressText}>
                <Typography variant="h6" color={COLORS.PRIMARY}>
                  {timeLeft}
                </Typography>
              </Box>
            </Box>

            {error ? (
              <Box sx={styles.errorBox}>
                <ErrorIcon color="error" />
                <Typography variant="body2" sx={styles.errorText}>
                  {error}
                </Typography>
              </Box>
            ) : (
              <>
                <Typography variant="body1" sx={{ color: COLORS.PRIMARY, fontWeight: 'bold', mb: 1 }}>
                  Tu sesión está a punto de expirar
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.TEXT_SECONDARY, mb: 4 }}>
                  ¿Deseas continuar en la sesión?
                </Typography>
              </>
            )}

            <Box sx={styles.buttonGroup}>
              <Button variant="outlined" sx={buttonStyles.outlined} onClick={handleLogout} disabled={isRefreshing}>
                No, cerrar sesión
              </Button>
              <Button
                variant="contained"
                sx={buttonStyles.contained}
                onClick={handleStayLoggedIn}
                disabled={isRefreshing || !!error}
                startIcon={isRefreshing ? <CircularProgress size={20} color="inherit" /> : null}
              >
                {isRefreshing ? 'Renovando...' : 'Sí, continuar'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default SessionClosed;
